import { createContext, FC, PropsWithChildren, useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { constructVimeoUrlFromVideoId, formatVideoTime, VimeoUrlParams } from './util';

type VimeoVideoOEmbedApiResponse = {
  type: string;
  version: string;
  provider_name: string;
  provider_url: string;
  title: string;
  author_name: string;
  author_url: string;
  is_plus: boolean;
  account_type: 'enterprise' | string;
  html: string;
  width: number;
  height: number;
  duration: number;
  description: string;
  thumbnail_url: string;
  thumbnail_width: number;
  thumbnail_height: number;
  thumbnail_url_with_play_button: string;
  upload_date: string;
  video_id: number;
  uri: string;
};

async function fetchVimeoData(videoUrl: string): Promise<VimeoVideoOEmbedApiResponse | null> {
  const apiUrl = `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(videoUrl)}`;

  try {
    const response = await fetch(apiUrl);
    if (!response.ok) {
      return null;
    }

    return await response.json();
  } catch (error) {
    return null;
  }
}

/**
 * Interface representing the context of a Vimeo video.
 *
 * @interface
 *
 * @property {string} [videoId] - The unique identifier for the Vimeo video.
 * @property {VimeoUrlParams} embedOptions - The options used for embedding the video.
 * @property {string | null} iframeEmbedUrl - The URL for embedding the video using an iframe.
 * @property {VimeoVideoOEmbedApiResponse | null | undefined} oembedApiData - The data returned from the oEmbed API for the video.
 * @property {string} [formattedDuration] - The formatted duration of the video.
 * @property {typeof formatVideoTime} [durationFormatter] - A function to format the video's duration.
 */
interface VimeoVideoContextInterface {
  videoId?: string;
  embedOptions: VimeoUrlParams;
  iframeEmbedUrl: string | null;
  oembedApiData: VimeoVideoOEmbedApiResponse | null | undefined;
  formattedDuration?: string;
  durationFormatter?: typeof formatVideoTime;
  videoDataProvider?: (
    videoId: string
  ) => Promise<VimeoVideoOEmbedApiResponse | null> | VimeoVideoOEmbedApiResponse | null;
}

const VideoVimeoContext = createContext<VimeoVideoContextInterface | undefined>(undefined);

const VideoVimeoProvider: FC<
  PropsWithChildren &
    Pick<VimeoVideoContextInterface, 'videoId' | 'embedOptions' | 'durationFormatter' | 'videoDataProvider'>
> = ({ children, embedOptions, videoId, durationFormatter = formatVideoTime, videoDataProvider }) => {
  const videoUrl = constructVimeoUrlFromVideoId(videoId, embedOptions);
  const { data: videoData } = useQuery({
    queryKey: ['vimeoApi', videoId],
    queryFn: () => {
      if (videoDataProvider && videoId) {
        return videoDataProvider(videoId);
      }
      if (videoUrl) {
        return fetchVimeoData(videoUrl);
      }
    },
  });

  return (
    <VideoVimeoContext.Provider
      value={{
        embedOptions,
        videoId,
        oembedApiData: videoData,
        iframeEmbedUrl: videoUrl,
        formattedDuration: durationFormatter?.(videoData?.duration),
        durationFormatter,
        videoDataProvider,
      }}
    >
      {children}
    </VideoVimeoContext.Provider>
  );
};

/**
 * A custom React hook that provides access to the Vimeo video context.
 * This hook must be used within a VideoVimeoContext provider, otherwise it will throw an error.
 *
 * @returns {VimeoVideoContextInterface} context - The Vimeo video context.
 * @throws {Error} If the hook is used outside a VideoVimeoContext provider.
 */
const useVimeoVideoContext = (): VimeoVideoContextInterface => {
  const context = useContext(VideoVimeoContext);
  if (context === undefined) {
    throw new Error('useVimeoVideoContext must be used within an VideoVimeoContext');
  }
  return context;
};

export { VideoVimeoProvider, useVimeoVideoContext };
